package wat.ui

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.*
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*

object ShareCard {

    // Generate a share card as canvas, then trigger Web Share API or download
    fun shareActivities(sports: List<SportEntry>, date: String) {
        val lines = mutableListOf<Pair<String, String>>() // icon+name, value
        sports.forEach { se ->
            val sp = wat.service.DataService.sports.find { it.k == se.k }
            val icon = sp?.i ?: ""
            val name = sp?.localName() ?: se.k
            val unit = I18n.tU(se.mode.ifBlank { sp?.u ?: "min" })
            val v = if (se.`val` == kotlin.math.floor(se.`val`)) fmt0(se.`val`) else fmt1(se.`val`)
            lines.add("$icon $name" to "$v $unit")
        }
        // Estimate calories
        val profile = wat.service.DataService.profile
        val w = wat.service.DataService.entries.find { it.date == date }?.weight ?: 80.0
        val age = if (profile.birthdate.isNotBlank()) calcAge(profile.birthdate) else 30
        var totalCal = 0
        sports.forEach { se ->
            val sp = wat.service.DataService.sports.find { it.k == se.k }
            val u = se.mode.ifBlank { sp?.u ?: "min" }
            val mins = if (u == "min") se.`val` else se.`val` * 60.0 // rough convert for non-min units
            totalCal += wat.service.DataService.estimateCalories(se.k, if (u == "min") se.`val` else se.`val`, w, age, profile.sex)
        }

        renderAndShare(t("share_today_activities"), formatDateFull(date), lines, if (totalCal > 0) "\u2248 $totalCal kcal" else null)
    }

    fun shareWeight(current: Double, diff: Double?, goalWeight: Double, bmi: Double, date: String) {
        val lines = mutableListOf<Pair<String, String>>()
        lines.add("\u2696\uFE0F ${t("weight_current")}" to "${fmt1(current)} kg")
        if (diff != null) {
            val sign = if (diff > 0) "+" else if (diff < 0) "\u2212" else "\u00B1"
            lines.add("\uD83D\uDCC9 ${t("weight_change")}" to "$sign${fmt1(kotlin.math.abs(diff))} kg")
        }
        if (bmi > 0) {
            lines.add("\uD83D\uDCCA BMI" to fmt1(bmi))
        }
        if (goalWeight > 0) {
            lines.add("\uD83C\uDFAF ${t("goal_title")}" to "${fmt1(goalWeight)} kg")
        }
        renderAndShare(t("share_weight_progress"), formatDateFull(date), lines, null)
    }

    private fun renderAndShare(title: String, subtitle: String, lines: List<Pair<String, String>>, footer: String?) {
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        val w = 600
        val lineH = 44
        val h = 160 + lines.size * lineH + if (footer != null) 50 else 20
        canvas.width = w
        canvas.height = h
        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D

        // Background gradient
        val bg = ctx.createLinearGradient(0.0, 0.0, w.toDouble(), h.toDouble())
        bg.addColorStop(0.0, "#111118")
        bg.addColorStop(1.0, "#1a1a2e")
        ctx.fillStyle = bg
        roundRect(ctx, 0.0, 0.0, w.toDouble(), h.toDouble(), 24.0)
        ctx.fill()

        // Subtle border
        ctx.strokeStyle = "rgba(255,255,255,0.08)"
        ctx.lineWidth = 1.0
        roundRect(ctx, 0.5, 0.5, w - 1.0, h - 1.0, 24.0)
        ctx.stroke()

        // WAT logo
        ctx.font = "700 28px 'Space Grotesk', sans-serif"
        val logoGrad = ctx.createLinearGradient(24.0, 0.0, 120.0, 0.0)
        logoGrad.addColorStop(0.0, "#2dd4bf")
        logoGrad.addColorStop(0.5, "#818cf8")
        logoGrad.addColorStop(1.0, "#c084fc")
        ctx.fillStyle = logoGrad
        ctx.fillText("TRACTOR", 24.0, 42.0)

        // Title
        ctx.font = "800 20px 'Space Grotesk', sans-serif"
        ctx.fillStyle = "#fff"
        ctx.fillText(title, 24.0, 80.0)

        // Subtitle (date)
        ctx.font = "500 13px 'Space Grotesk', sans-serif"
        ctx.fillStyle = "rgba(255,255,255,0.35)"
        ctx.fillText(subtitle, 24.0, 100.0)

        // Lines
        var y = 130.0
        lines.forEach { (label, value) ->
            ctx.font = "500 15px 'Space Grotesk', sans-serif"
            ctx.fillStyle = "rgba(255,255,255,0.6)"
            ctx.fillText(label, 32.0, y)

            ctx.font = "800 16px 'Space Grotesk', sans-serif"
            ctx.fillStyle = "#fff"
            ctx.textAlign = CanvasTextAlign.RIGHT
            ctx.fillText(value, (w - 32).toDouble(), y)
            ctx.textAlign = CanvasTextAlign.LEFT

            // separator line
            ctx.strokeStyle = "rgba(255,255,255,0.06)"
            ctx.lineWidth = 1.0
            ctx.beginPath()
            ctx.moveTo(32.0, y + 12.0)
            ctx.lineTo((w - 32).toDouble(), y + 12.0)
            ctx.stroke()

            y += lineH
        }

        // Footer (calories)
        if (footer != null) {
            ctx.font = "700 14px 'Space Grotesk', sans-serif"
            val calGrad = ctx.createLinearGradient(24.0, 0.0, 200.0, 0.0)
            calGrad.addColorStop(0.0, "#2dd4bf")
            calGrad.addColorStop(1.0, "#818cf8")
            ctx.fillStyle = calGrad
            ctx.fillText(footer, 32.0, y + 8.0)
        }

        // Try Web Share API, fallback to download
        try {
            canvas.asDynamic().toBlob({ blob: dynamic ->
                val file = js("new File([blob], 'tractor-share.png', {type: 'image/png'})")
                val shareData = js("{}")
                shareData.files = js("[file]")
                shareData.title = "TRACTOR - $title"
                val nav = window.navigator.asDynamic()
                if (nav.canShare != undefined && nav.canShare(shareData) == true) {
                    nav.share(shareData)
                } else {
                    downloadCanvas(canvas)
                }
            }, "image/png")
        } catch (_: Exception) {
            downloadCanvas(canvas)
        }
    }

    private fun downloadCanvas(canvas: HTMLCanvasElement) {
        val link = document.createElement("a") as HTMLAnchorElement
        link.download = "wat-share.png"
        link.href = canvas.toDataURL("image/png")
        link.click()
    }

    private fun roundRect(ctx: CanvasRenderingContext2D, x: Double, y: Double, w: Double, h: Double, r: Double) {
        ctx.beginPath()
        ctx.moveTo(x + r, y)
        ctx.lineTo(x + w - r, y)
        ctx.arcTo(x + w, y, x + w, y + r, r)
        ctx.lineTo(x + w, y + h - r)
        ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
        ctx.lineTo(x + r, y + h)
        ctx.arcTo(x, y + h, x, y + h - r, r)
        ctx.lineTo(x, y + r)
        ctx.arcTo(x, y, x + r, y, r)
        ctx.closePath()
    }

    // Reusable share button widget
    fun shareButton(onClick: () -> Unit): HTMLElement {
        return button("") {
            style.cssText = "width:30px;height:30px;border-radius:8px;border:none;background:${Styles.CARD_BG};cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
            title = t("share_btn")
            innerHTML = "<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='${Styles.A1}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8'/><polyline points='16 6 12 2 8 6'/><line x1='12' y1='2' x2='12' y2='15'/></svg>"
            onclick = { onClick() }
        }
    }
}
